/**
 * AssessmentPage — 12-question diagnostic conversation
 *
 * Led by Philemon (Part 1), Xenia (Part 2), and Theron (Part 3).
 * One question at a time with character dialogue framing each question.
 */

import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

import {
  ASSESSMENT_QUESTIONS,
  ASSESSMENT_INTRO,
  ASSESSMENT_CLOSING,
  PART_TRANSITIONS,
  calculateScores,
  getRecommendedModules,
} from '../data/assessment';
import CharacterDialogue from '../components/characters/CharacterDialogue';
import CharacterAvatar from '../components/characters/CharacterAvatar';
import Card from '../components/shared/Card';
import Button from '../components/shared/Button';
import { useProgress } from '../context/ProgressContext';
import { useGamification } from '../context/GamificationContext';
import { recordAssessmentComplete } from '../services/analytics';

const TOTAL_QUESTIONS = ASSESSMENT_QUESTIONS.length;

const slideVariants = {
  enter: { opacity: 0, x: 60 },
  centre: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -60 },
};

const AssessmentPage = () => {
  const navigate = useNavigate();
  const { saveAssessmentResults, assessmentCount, getAchievementContext } = useProgress();
  const { addXP, checkAchievements } = useGamification();

  const [phase, setPhase] = useState('intro'); // intro | question | transition | closing
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});

  const question = ASSESSMENT_QUESTIONS[currentIndex];
  // partTitle is only authored on the first question of each part; resolve it
  // from there so the progress label stays populated for the whole part.
  const partTitle = question
    ? ASSESSMENT_QUESTIONS.find((q) => q.part === question.part)?.partTitle
    : null;

  // Determine which transition to show (if any) before this question
  const prevQuestion = currentIndex > 0 ? ASSESSMENT_QUESTIONS[currentIndex - 1] : null;
  const transitionKey =
    prevQuestion && prevQuestion.part !== question?.part
      ? `${prevQuestion.part}-to-${question.part}`
      : null;

  const handleSelect = useCallback(
    (questionId, optionId, isMulti) => {
      setAnswers((prev) => {
        if (isMulti) {
          const current = prev[questionId] || [];
          if (current.includes(optionId)) {
            return { ...prev, [questionId]: current.filter((id) => id !== optionId) };
          }
          return { ...prev, [questionId]: [...current, optionId] };
        }
        return { ...prev, [questionId]: optionId };
      });
    },
    []
  );

  const canProceed = () => {
    if (!question) return false;
    const answer = answers[question.id];
    if (question.type === 'multi') return Array.isArray(answer) && answer.length > 0;
    return !!answer;
  };

  const handleNext = () => {
    if (currentIndex < TOTAL_QUESTIONS - 1) {
      const nextQuestion = ASSESSMENT_QUESTIONS[currentIndex + 1];
      // Advance the index in the same step as the phase change: transitionKey
      // is derived from the previous vs current question's part, so the index
      // must already sit on the new part for the transition to resolve.
      setCurrentIndex((prev) => prev + 1);
      if (question.part !== nextQuestion.part) {
        setPhase('transition');
      }
    } else {
      // Assessment complete
      setPhase('closing');
    }
  };

  const handleTransitionDone = () => {
    setPhase('question');
  };

  const handleComplete = () => {
    const scores = calculateScores(answers);
    const recommended = getRecommendedModules(answers, scores);
    saveAssessmentResults({ answers, scores, recommended });
    addXP(50, 'Assessment completed');
    recordAssessmentComplete(); // counts the event only; no answers or scores are sent
    checkAchievements(getAchievementContext({
      assessmentDone: true,
      assessmentCount: assessmentCount + 1,
    }));
    navigate('/results');
  };

  // ── Intro Phase ──
  if (phase === 'intro') {
    return (
      <>
        <Helmet>
          <title>Assessment | Finding Your Voice</title>
        </Helmet>
        <div className="max-w-2xl mx-auto px-4 py-12">
          <motion.div
            className="space-y-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="font-display text-3xl font-bold text-courtyard-stone text-center mb-8">
              The Assessment
            </h1>
            <div className="space-y-4">
              {ASSESSMENT_INTRO.lines.map((line, i) =>
                line.character ? (
                  <CharacterDialogue
                    key={i}
                    characterId={line.character.toLowerCase()}
                    speaker={line.character}
                    text={line.text}
                  />
                ) : (
                  <CharacterDialogue
                    key={i}
                    isNarration
                    text={line.text}
                  />
                )
              )}
            </div>
            <div className="text-center pt-6">
              <Button
                variant="primary"
                size="lg"
                onClick={() => setPhase('question')}
              >
                I&rsquo;m Ready
              </Button>
            </div>
          </motion.div>
        </div>
      </>
    );
  }

  // ── Transition Phase ──
  if (phase === 'transition' && transitionKey && PART_TRANSITIONS[transitionKey]) {
    const transition = PART_TRANSITIONS[transitionKey];
    return (
      <>
        <Helmet>
          <title>Assessment | Finding Your Voice</title>
        </Helmet>
        <div className="max-w-2xl mx-auto px-4 py-12">
          <motion.div
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            {transition.lines.map((line, i) =>
              line.character ? (
                <CharacterDialogue
                  key={i}
                  characterId={line.character.toLowerCase()}
                  speaker={line.character}
                  text={line.text}
                />
              ) : (
                <CharacterDialogue key={i} isNarration text={line.text} />
              )
            )}
            <div className="text-center pt-6">
              <Button variant="primary" onClick={handleTransitionDone}>
                Continue
              </Button>
            </div>
          </motion.div>
        </div>
      </>
    );
  }

  // ── Closing Phase ──
  if (phase === 'closing') {
    return (
      <>
        <Helmet>
          <title>Assessment Complete | Finding Your Voice</title>
        </Helmet>
        <div className="max-w-2xl mx-auto px-4 py-12">
          <motion.div
            className="space-y-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6 }}
          >
            {ASSESSMENT_CLOSING.lines.map((line, i) =>
              line.character ? (
                <CharacterDialogue
                  key={i}
                  characterId={line.character.toLowerCase()}
                  speaker={line.character}
                  text={line.text}
                />
              ) : (
                <CharacterDialogue key={i} isNarration text={line.text} />
              )
            )}
            <div className="text-center pt-6">
              <Button variant="primary" size="lg" onClick={handleComplete}>
                See My Results
              </Button>
            </div>
          </motion.div>
        </div>
      </>
    );
  }

  // ── Question Phase ──
  if (!question) return null;

  const isMulti = question.type === 'multi';
  const selectedAnswer = answers[question.id];

  return (
    <>
      <Helmet>
        <title>{`Assessment, question ${question.questionNumber} | Finding Your Voice`}</title>
      </Helmet>

      <div className="max-w-2xl mx-auto px-4 py-8">
        {/* Progress Bar */}
        <div className="mb-8">
          <div className="flex justify-between text-xs text-courtyard-stone/80 mb-1">
            <span>Question {question.questionNumber} of {TOTAL_QUESTIONS}</span>
            <span>Part {question.part}{partTitle ? `: ${partTitle}` : ''}</span>
          </div>
          <div className="h-2 bg-courtyard-stone/10 rounded-full overflow-hidden">
            <motion.div
              className="h-full bg-courtyard-gold rounded-full"
              initial={false}
              animate={{
                width: `${(question.questionNumber / TOTAL_QUESTIONS) * 100}%`,
              }}
              transition={{ duration: 0.3 }}
            />
          </div>
        </div>

        <AnimatePresence mode="wait">
          <motion.div
            key={question.id}
            variants={slideVariants}
            initial="enter"
            animate="centre"
            exit="exit"
            transition={{ duration: 0.35 }}
            className="space-y-6"
          >
            {/* Part intro (shown on first question of each part) */}
            {question.partIntro && (
              <p className="text-courtyard-stone/80 italic text-sm">
                {question.partIntro}
              </p>
            )}

            {/* Character & prompt */}
            <div className="flex items-start gap-3">
              <CharacterAvatar characterId={question.character} size="md" />
              <div className="flex-1">
                {question.intro && (
                  <p className="text-courtyard-stone/80 italic text-sm mb-2">
                    {question.intro}
                  </p>
                )}
                <p className="text-courtyard-stone font-medium leading-relaxed">
                  &ldquo;{question.prompt}&rdquo;
                </p>
              </div>
            </div>

            {/* Options */}
            <div className="space-y-3">
              {question.options.map((option) => {
                const isSelected = isMulti
                  ? (selectedAnswer || []).includes(option.id)
                  : selectedAnswer === option.id;

                return (
                  <button
                    key={option.id}
                    onClick={() => handleSelect(question.id, option.id, isMulti)}
                    className={`w-full text-left transition-all duration-200 ${
                      isSelected
                        ? 'ring-2 ring-courtyard-gold bg-courtyard-gold/10'
                        : 'hover:bg-courtyard-stone/5'
                    }`}
                  >
                    <Card className="p-4 flex items-start gap-3">
                      <span className="text-xl flex-shrink-0">{option.emoji}</span>
                      <span className="text-sm text-courtyard-stone/80 leading-relaxed">
                        {option.text}
                      </span>
                    </Card>
                  </button>
                );
              })}
            </div>

            {isMulti && (
              <p className="text-xs text-courtyard-stone/80 text-center">
                Select all that apply
              </p>
            )}

            {/* Next */}
            <div className="text-center pt-4">
              <Button
                variant="primary"
                onClick={handleNext}
                disabled={!canProceed()}
              >
                {currentIndex < TOTAL_QUESTIONS - 1 ? 'Next' : 'Finish'}
              </Button>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </>
  );
};

export default AssessmentPage;
